// Importar las bibliotecas necesarias de Firebase
import { initializeApp } from 'firebase/app';
import { useState, useEffect, useCallback, useRef } from 'react';
import { getFirestore, collection, addDoc, getDocs, query, where,doc, updateDoc, deleteDoc, getDoc, setDoc, orderBy } from 'firebase/firestore';
import { getDatabase, ref, onValue, onDisconnect, set, serverTimestamp, get } from 'firebase/database';


// Configuración de Firebase
const firebaseConfig = {
  apiKey: "AIzaSyACFkOlclCpQlSytfjVeHbS2qr0rNISMdE",
  authDomain: "amgroupradio.firebaseapp.com",
  projectId: "amgroupradio",
  storageBucket: "amgroupradio.appspot.com",
  messagingSenderId: "982611601502",
  appId: "1:982611601502:web:afe4e482f67051a491eccf",
  measurementId: "G-5P7H1184DP"
};

// Inicializar Firebase
const app = initializeApp(firebaseConfig);

// Obtener una instancia de Firestore
const db = getFirestore(app);

// Función para registrar un nuevo usuario
const registerUser = async (userId, password, name, server, level) => {
  userId = userId.toLowerCase();  // Convertir a minúsculas
  
  // Comprobar si el ID de usuario ya existe
  const existingUserQuery = query(
    collection(db, "users"),
    where("userId", "==", userId)
  );
  const existingUserSnapshot = await getDocs(existingUserQuery);
  
  if (!existingUserSnapshot.empty) {
    return { success: false, message: "El ID de usuario ya existe" };
  }

  try {
    await addDoc(collection(db, "users"), {
      userId,
      password,
      name,
      server,
      level
    });
    return { success: true, message: "Registro exitoso" };
  } catch (error) {
    return { success: false, message: "Error al registrar: " + error.message };
  }
};

const editUserInfo = async (userId, updates) => {
  const userDocRef = doc(db, "users", userId);
  try {
    await updateDoc(userDocRef, updates);
    return { success: true, message: "Información del usuario actualizada correctamente" };
  } catch (error) {
    return { success: false, message: "Error al actualizar la información del usuario: " + error.message };
  }
};

const deleteUser = async (userId) => {
  const userDocRef = doc(db, "users", userId);
  try {
    await deleteDoc(userDocRef);
    return { success: true, message: "Usuario eliminado correctamente" };
  } catch (error) {
    return { success: false, message: "Error al eliminar el usuario: " + error.message };
  }
};

const getUsersByServer = async (serverName) => {
  try {
    if (!serverName) {
      ////console.error('Server name is required');
      return [];
    }

    const serverNameLower = serverName.toLowerCase();
    const q = query(collection(db, "users"), where("server", "==", serverNameLower));
    
    try {
      const querySnapshot = await getDocs(q);
      const usersList = querySnapshot.docs.map(doc => ({
        id: doc.id, // Agregar el ID del documento por si necesitas referenciar al usuario específico después
        ...doc.data() // Desestructurar los datos del usuario para obtener todos los campos
      }));
      return { success: true, usersList };
    } catch (error) {
      ////console.error("Error al obtener los usuarios:", error);
      return { success: false, message: "Error al obtener los usuarios: " + error.message, usersList: [] };
    }
  } catch (error) {
    ////console.error('Error in getUsersByServer:', error);
    throw error;
  }
};


// Función para iniciar sesión de un usuario
const loginUser = async (userId, password) => {
  userId = userId.toLowerCase();  // Convertir a minúsculas
  const q = query(
    collection(db, "users"),
    where("userId", "==", userId),
    where("password", "==", password)
  );
  
  const querySnapshot = await getDocs(q);
  
  if (querySnapshot.empty) {
    return { success: false, message: "ID de usuario o contraseña inválidos" };
  } else {
    return { success: true, userData: querySnapshot.docs[0].data() };
    
  }
};

// Mover estas funciones fuera del hook usePresence
const createConnectionLog = async (radioName, connectionData) => {
  try {
    console.log('📝 Iniciando creación de log de conexión:', {
      radioName,
      connectionData
    });

    const formattedRadioName = radioName.toLowerCase().trim();
    
    // Crear referencia a la colección de radios
    const radiosRef = collection(db, 'radios');
    const radioRef = doc(radiosRef, formattedRadioName);
    
    // Verificar si existe el documento de la radio
    const radioDoc = await getDoc(radioRef);
    
    if (!radioDoc.exists()) {
      console.log('📌 Creando nuevo documento de radio:', formattedRadioName);
      const now = new Date().getTime();
      await setDoc(radioRef, {
        name: formattedRadioName,
        created: now,
        lastUpdated: now
      });
    }

    // Crear la conexión en la subcolección
    const connectionsRef = collection(radioRef, 'connections');
    const now = new Date().getTime();
    
    const connectionDoc = await addDoc(connectionsRef, {
      ...connectionData,
      startTime: now,
      status: 'active',
      created: now,
      lastUpdated: now
    });

    console.log('✅ Conexión registrada exitosamente:', {
      radio: formattedRadioName,
      connectionId: connectionDoc.id,
      data: {
        ...connectionData,
        startTime: now,
        created: now,
        lastUpdated: now
      }
    });

    return connectionDoc;
  } catch (error) {
    console.error('❌ Error creando registro de conexión:', error);
    throw error;
  }
};

const updateConnectionLog = async (radioName, connectionId, updates) => {
  try {
    const formattedRadioName = radioName.toLowerCase().trim();
    const connectionRef = doc(db, 'radios', formattedRadioName, 'connections', connectionId);
    
    const now = new Date().getTime();
    await updateDoc(connectionRef, {
      ...updates,
      lastUpdated: now
    });

    console.log('Conexión actualizada:', {
      radio: formattedRadioName,
      connectionId,
      updates: {
        ...updates,
        lastUpdated: now
      }
    });
  } catch (error) {
    console.error('Error actualizando registro de conexión:', error);
    throw error;
  }
};

const getRadioConnections = async (radioName, options = {}) => {
  try {
    const formattedRadioName = radioName.toLowerCase().trim();
    const connectionsRef = collection(db, 'radios', formattedRadioName, 'connections');
    
    let q = query(connectionsRef);

    if (options.startDate) {
      q = query(q, where('startTime', '>=', options.startDate));
    }
    if (options.endDate) {
      q = query(q, where('startTime', '<=', options.endDate));
    }
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }

    q = query(q, orderBy('startTime', 'desc'));

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error obteniendo conexiones:', error);
    throw error;
  }
};

const usePresence = (initialRadio) => {
  const [clientName, setClientName] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [radio, setRadio] = useState('');
  const [location, setLocation] = useState(null);
  const firebase = getDatabase(app);

  // Referencia al último presenceRef activo
  const activePresenceRef = useRef(null);

  // Función para limpiar la presencia actual
  const clearPresence = useCallback(async () => {
    if (clientName && radio) {
      const presenceRef = ref(firebase, '/presence/' + radio.toLowerCase() + '/' + clientName);
      try {
        const snapshot = await get(presenceRef);
        if (snapshot.exists()) {
          await set(presenceRef, {
            ...snapshot.val(),
            online: false,
            lastOnline: serverTimestamp(),
            last_changed: serverTimestamp()
          });
        }
        //console.log('🔴 [clearPresence] Estado actualizado a offline');
      } catch (error) {
        ////console.error('Error clearing presence:', error);
      }
    }
    // Reiniciar el estado local
    setClientName('');
    setIsOnline(false);
    setRadio('');
    setLocation(null);
  }, [clientName, radio, firebase]);

  // Efecto para limpiar la presencia cuando cambia el usuario
  useEffect(() => {
    return () => {
      clearPresence();
    };
  }, [clearPresence]);

  const handleNameSubmit = useCallback((name) => {
    return new Promise((resolve) => {
      if (name && name !== 'Usuario Externo') {
        //console.log('Setting client name:', name);
        setClientName(name);
        // Esperar a que el estado se actualice
        setTimeout(() => {
          //console.log('Client name updated to:', name);
          resolve(name);
        }, 100);
      } else {
        resolve(null);
      }
    });
  }, []);

  const handleRadioChange = useCallback((newRadio) => {
    return new Promise((resolve) => {
      if (newRadio) {
        //console.log('Setting radio:', newRadio);
        setRadio(newRadio);
        // Esperar a que el estado se actualice
        setTimeout(() => {
          //console.log('Radio updated to:', newRadio);
          resolve(newRadio);
        }, 100);
      } else {
        resolve(null);
      }
    });
  }, []);

  // Modificar getGeolocation para que retorne una promesa
  const getGeolocation = () => {
    return new Promise(async (resolve) => {
      if (!navigator.geolocation) {
        console.log('Geolocalización no soportada en este navegador');
        resolve(null);
        return;
      }

      try {
        // Primero verificamos el estado de los permisos
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
        console.log('Estado de permisos de geolocalización:', permissionStatus.state);

        if (permissionStatus.state === 'denied') {
          console.log('Permisos de geolocalización denegados por el usuario');
          resolve(null);
          return;
        }

        const options = {
          enableHighAccuracy: true,
          timeout: 10000, // Aumentamos el timeout a 10 segundos
          maximumAge: 300000 // Permitimos cache de 5 minutos
        };

        const success = (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          };
          console.log('Ubicación obtenida exitosamente:', locationData);
          setLocation(locationData); // Guardamos en el estado
          resolve(locationData);
        };

        const error = (err) => {
          console.warn(`Error de geolocalización (${err.code}):`, {
            message: err.message,
            code: err.code,
            PERMISSION_DENIED: err.PERMISSION_DENIED,
            POSITION_UNAVAILABLE: err.POSITION_UNAVAILABLE,
            TIMEOUT: err.TIMEOUT
          });
          resolve(null);
        };

        // Intentamos obtener la ubicación
        navigator.geolocation.getCurrentPosition(success, error, options);
      } catch (error) {
        console.error('Error inesperado al obtener geolocalización:', error);
        resolve(null);
      }
    });
  };

  // Efecto para obtener la geolocalización cuando el componente se monta
  useEffect(() => {
    getGeolocation();
  }, []);

  // Función para configurar la presencia inicial
  const registerInFirebase = useCallback(async (userName, radioPath) => {
    //console.log('🔵 [registerInFirebase] Iniciando con:', { userName, radioPath });

    if (!userName || !radioPath || userName === 'Usuario Externo') {
      //console.log('❌ [registerInFirebase] Datos faltantes:', { userName, radioPath });
      return;
    }

    const presenceRef = ref(firebase, '/presence/' + radioPath.toLowerCase() + '/' + userName);
    //console.log('📍 [registerInFirebase] Path:', presenceRef.toString());

    try {
      // Obtener geolocalización
      const locationData = await getGeolocation();
      //console.log('📍 [registerInFirebase] Ubicación obtenida:', locationData);

      // Estado base
      const baseState = {
        clientName: userName,
        ip: '',
        last_changed: serverTimestamp(),
        lastOnline: serverTimestamp(),
        online: false, // Inicialmente false
        location: locationData
      };

      // Obtener IP
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        baseState.ip = ipData.ip;
      } catch (error) {
        //console.error('Error getting IP:', error);
      }

      // Configurar onDisconnect
      const offlineState = {
        ...baseState,
        online: false,
        lastOnline: serverTimestamp(),
        last_changed: serverTimestamp()
      };

      await onDisconnect(presenceRef).set(offlineState);
      //console.log('🔄 [registerInFirebase] onDisconnect configurado');

      // Registrar estado inicial
      await set(presenceRef, baseState);
      //console.log('💾 [registerInFirebase] Estado inicial registrado');

      return baseState;
    } catch (error) {
      //console.error('❌ [registerInFirebase] Error:', error);
      return null;
    }
  }, [firebase]);

  // Función simple para actualizar solo el estado online
  const updateOnlineStatus = useCallback(async (userName, radioPath, online) => {
    //console.log(`${online ? '🟢' : '🔴'} [updateOnlineStatus] Actualizando estado:`, { userName, radioPath, online });

    if (!userName || !radioPath || userName === 'Usuario Externo') return;

    const presenceRef = ref(firebase, '/presence/' + radioPath.toLowerCase() + '/' + userName);

    try {
      await set(presenceRef, {
        ...(await get(presenceRef)).val(),
        online,
        last_changed: serverTimestamp(),
        ...(online === false && { lastOnline: serverTimestamp() })
      });

      setIsOnline(online);
      //console.log(`✅ [updateOnlineStatus] Estado actualizado a ${online ? 'online' : 'offline'}`);
    } catch (error) {
      //console.error('❌ [updateOnlineStatus] Error:', error);
    }
  }, [firebase]);

  const getUsers = async (radioname) => {
    radioname = radioname.toLowerCase();
    const usersRef = ref(firebase, '/presence/' + radioname);
    try {
      const snapshot = await get(usersRef);
      if (snapshot.exists()) {
        return snapshot.val();
      } else {
        //  ////console.log("No data available for: ", radioname);
        return null;
      }
    } catch (error) {
      //  ////console.error("Error fetching data:", error);
      return null;
    }
  };

  const setOnline = useCallback(async (userName, radioPath) => {
    console.log('🟢 Iniciando setOnline:', { userName, radioPath });
    
    if (!userName || !radioPath || userName === 'Usuario Externo') {
      return null;
    }

    const presenceRef = ref(firebase, '/presence/' + radioPath.toLowerCase() + '/' + userName);
    activePresenceRef.current = presenceRef;

    try {
      // Obtener IP primero
      let ipAddress = '';
      let geoipData = null;  // Declarar geoipData aquí
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      } catch (error) {
        console.error('Error obteniendo IP:', error);
      }

      // Intentar obtener geolocalización del navegador
      let locationData = await getGeolocation();
      console.log('Datos de localización del navegador:', locationData);

      // Verificar si ya existe una localización en Firebase
      const currentSnapshot = await get(presenceRef);
      const currentData = currentSnapshot.exists() ? currentSnapshot.val() : null;
      
      if (currentData?.location) {
        console.log('Localización existente en Firebase:', currentData.location);
        // Usar la localización existente si tiene menos de 1 hora
        const isLocationRecent = currentData.location.timestamp && 
          (Date.now() - currentData.location.timestamp < 3600000); // 1 hora
        
        if (isLocationRecent) {
          locationData = currentData.location;
          console.log('Usando localización existente de Firebase');
        }
      }

      // Si no hay localización del navegador ni de Firebase, intentar por IP
      if (!locationData && ipAddress) {
        try {
          const geoResponse = await fetch(`https://ipapi.co/${ipAddress}/json/`);
          geoipData = await geoResponse.json();  // Asignar a geoipData
          if (geoipData.latitude && geoipData.longitude) {
            locationData = {
              latitude: geoipData.latitude,
              longitude: geoipData.longitude,
              source: 'ip',
              timestamp: Date.now(),
              accuracy: 5000, // Precisión aproximada en metros para geolocalización por IP
              city: geoipData.city,
              region: geoipData.region,
              country: geoipData.country_name
            };
            console.log('Ubicación obtenida por IP:', locationData);
          }
        } catch (error) {
          console.error('Error obteniendo ubicación por IP:', error);
        }
      }

      // Antes de crear el registro en Firestore
      console.log('📊 Datos a guardar en Firestore:', {
        clientName: userName,
        ip: ipAddress,
        location: locationData,
        userAgent: navigator.userAgent,
        geodata: locationData?.source === 'ip' ? {
          city: locationData.city || 'Desconocido',
          region: locationData.region || '',
          country: locationData.country || ''
        } : (locationData ? {
          city: 'Ubicación del navegador',
          region: '',
          country: ''
        } : null)
      });

      // Crear registro en Firestore
      const connectionLog = await createConnectionLog(radioPath, {
        clientName: userName,
        ip: ipAddress,
        location: locationData,
        userAgent: navigator.userAgent,
        geodata: locationData?.source === 'ip' ? {
          city: locationData.city || 'Desconocido',
          region: locationData.region || '',
          country: locationData.country || ''
        } : (locationData ? {
          city: 'Ubicación del navegador',
          region: '',
          country: ''
        } : null),
        metadata: {
          browser: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language
        }
      });

      console.log('📝 Registro de conexión creado:', connectionLog);

      // Guardar el ID de conexión en el estado de presencia
      const baseState = {
        clientName: userName,
        ip: ipAddress,
        online: true,
        connectionId: connectionLog.id,
        last_changed: serverTimestamp(),
        ...(locationData && { location: locationData })
      };

      await set(presenceRef, baseState);
      
      // Configurar onDisconnect
      await onDisconnect(presenceRef).update({
        online: false,
        lastOnline: serverTimestamp(),
        last_changed: serverTimestamp()
      });

      setIsOnline(true);
      setClientName(userName);
      setRadio(radioPath);

      return baseState;
    } catch (error) {
      console.error('❌ Error en setOnline:', error);
      return null;
    }
  }, [firebase, getGeolocation]);

  const setOffline = useCallback(async (userName, radioPath) => {
    //console.log('🔴 [setOffline] Iniciando para:', { userName, radioPath });

    if (!userName || !radioPath || userName === 'Usuario Externo') {
      //console.log('❌ [setOffline] Datos faltantes:', { userName, radioPath });
      return null;
    }

    const presenceRef = ref(firebase, '/presence/' + radioPath.toLowerCase() + '/' + userName);

    try {
      // Cancelar onDisconnect al poner offline manualmente
      await onDisconnect(presenceRef).cancel();
      //console.log('🔄 [setOffline] onDisconnect cancelado');

      const snapshot = await get(presenceRef);
      if (snapshot.exists()) {
        const currentData = snapshot.val();
        const offlineData = {
          ...currentData,
          online: false,
          lastOnline: serverTimestamp(),
          last_changed: serverTimestamp()
        };

        //console.log('📝 [setOffline] Actualizando datos:', offlineData);
        await set(presenceRef, offlineData);

        // Verificación inmediata
        const verifySnapshot = await get(presenceRef);
        //console.log('✅ [setOffline] Verificación inmediata:', verifySnapshot.val());

        setIsOnline(false);
        setClientName(userName);
        setRadio(radioPath);

        // Actualizar el registro en Firestore
        if (currentData.connectionId) {
          await updateConnectionLog(radioPath, currentData.connectionId, {
            endTime: serverTimestamp(),
            status: 'ended',
            disconnectReason: 'manual'
          });
        }

        return verifySnapshot.val();
      }
      return null;
    } catch (error) {
      console.error('Error en setOffline:', error);
      return null;
    }
  }, [firebase]);

  // Efecto para manejar la desconexión real
  useEffect(() => {
    const handleBeforeUnload = () => {
      //console.log('🔴 [beforeunload] Desconexión detectada');
      if (activePresenceRef.current) {
        //console.log('📍 [beforeunload] Actualizando estado para:', activePresenceRef.current.toString());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
        console.log('Estado inicial de permisos:', permissionStatus.state);
        
        permissionStatus.addEventListener('change', () => {
          console.log('Cambio en permisos de geolocalización:', permissionStatus.state);
          if (permissionStatus.state === 'granted') {
            getGeolocation();
          }
        });
      } catch (error) {
        console.error('Error verificando permisos:', error);
      }
    };

    checkPermissions();
  }, []);

  return {
    isOnline,
    clientName,
    handleNameSubmit,
    handleRadioChange,
    getUsers,
    registerInFirebase,
    updateOnlineStatus,
    setOnline,
    setOffline,
    clearPresence
  };
};



export { app, db, registerUser, loginUser,usePresence, editUserInfo, deleteUser, getUsersByServer, createConnectionLog, updateConnectionLog, getRadioConnections  };